import { PressableNeolexon } from '@/components/PressableNeolexon';
import colors from '@/config/colors';
import FontAwesome from '@expo/vector-icons/FontAwesome';
import { AVPlaybackStatus, ResizeMode, Video } from 'expo-av';
import { ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import {
  ActivityIndicator,
  GestureResponderEvent,
  StyleProp,
  StyleSheet,
  View,
  ViewStyle,
} from 'react-native';
import { useFailSafe } from '@/hooks/useFailSafe';
import { useNotification } from '@/components/ui/therapist/Notification/NotificationProvider';

const styles = StyleSheet.create({
  wrapper: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
  videoContainer: {
    justifyContent: 'center',
    alignItems: 'center',
  },
  audioStyles: {
    backgroundColor: colors.darkPurpleBlue,
    justifyContent: 'center',
    alignItems: 'center',
  },
  questionStyles: {
    backgroundColor: colors.darkPurpleBlue,
    justifyContent: 'center',
    alignItems: 'center',
  },
  disabled: {
    opacity: 0.3,
  },
  none: {
    display: 'none',
  },
  center: { justifyContent: 'center', alignItems: 'center' },
});

let timer: number | undefined | NodeJS.Timeout = undefined;
let skipTimer: number | undefined | NodeJS.Timeout = undefined;

export function VideoPlayer({
  url,
  noStyles,
  customStyles,
  noVideo,
  correctSelected,
  handleIsVideoPlaying,
  isRecording,
  isAudioPlaying,
  playOnMount,
  autoPlay,
  onCorrect,
  children,
  playbackRate = 1.0,
}: {
  url: string;
  noStyles?: boolean;
  customStyles?: StyleProp<ViewStyle>;
  noVideo?: boolean;
  correctSelected: boolean;
  handleIsVideoPlaying: (v?: boolean) => void;
  isRecording: boolean;
  isAudioPlaying: boolean;
  playOnMount: boolean;
  autoPlay: boolean;
  onCorrect?: (v?: any) => void;
  children: ReactNode;
  playbackRate?: number;
}) {
  const hasFiredRef = useRef<boolean>(false);
  const durationRef = useRef<number>(0);
  const positionRef = useRef<number>(0);
  const [showVideo, setShowVideo] = useState(false);
  const [noVideoState, setNoVideoState] = useState(noVideo);
  const [isBuffering, setIsBuffering] = useState(false);
  const [containerSize, setContainerSize] = useState(0);
  const { showNotification } = useNotification();
  const videoRef = useRef<Video>(null);

  useFailSafe({
    isCorrectSelected: correctSelected,
    getDurationAndPosition: () => ({
      duration: durationRef.current ?? 0,
      position: positionRef.current ?? 0,
    }),
    onTrigger: () => {
      handleVideoFinish();
      onCorrect?.();
    },
    hasFiredRef,
  });

  const disabled = isRecording || isAudioPlaying;

  const playUrl = useCallback(
    async (event?: GestureResponderEvent | '_AUTO_PLAY') => {
      try {
        if (!url) {
          return;
        }
        if (event !== '_AUTO_PLAY') {
          clearTimeout(timer);
          await videoRef.current?.setStatusAsync({
            rate: correctSelected ? 1 : playbackRate,
            positionMillis: 0.1,
            shouldPlay: false,
          });
          await videoRef.current?.playAsync();
          setShowVideo(true);

          if (handleIsVideoPlaying) {
            handleIsVideoPlaying(true);
          }
        } else {
          timer = setTimeout(() => {
            setShowVideo(true);
            videoRef.current?.setStatusAsync({
              rate: correctSelected ? 1 : playbackRate,
              positionMillis: 0.1,
              shouldPlay: false,
            });
            videoRef.current?.playAsync();
            if (handleIsVideoPlaying) {
              handleIsVideoPlaying(true);
            }
          }, 1000);
        }
      } catch {
        showNotification({
          message: 'Es gab ein Problem beim Abspielen der Datei.',
          type: 'error',
        });
      }
    },
    [correctSelected],
  );

  const handleVideoFinish = useCallback(() => {
    setShowVideo(false);
    if (handleIsVideoPlaying) {
      handleIsVideoPlaying(false);
    }
  }, []);

  const onPlaybackStatusUpdate = useCallback(
    (status: AVPlaybackStatus) => {
      if (!status.isLoaded) {
        return;
      }
      durationRef.current = status.durationMillis ?? 0;
      positionRef.current = status.positionMillis ?? 0;

      setIsBuffering(status.isBuffering);
      if (status.didJustFinish) {
        handleVideoFinish();
        clearTimeout(skipTimer);
        hasFiredRef.current = true;
        if (correctSelected) {
          skipTimer = setTimeout(() => {
            onCorrect?.();
          }, 1000);
        }
      }
    },
    [correctSelected],
  );

  const playAudioOnly = useCallback(() => {
    setNoVideoState(true);
    playUrl();
  }, [correctSelected]);

  useEffect(() => {
    if (correctSelected) {
      playAudioOnly();
    } else {
      handleVideoFinish();
      setNoVideoState(noVideo);
    }
    if (playOnMount) {
      playAudioOnly();
    }
  }, [correctSelected]);

  useEffect(() => {
    if (autoPlay) {
      playUrl('_AUTO_PLAY');
    }
  }, [url]);

  // Handle layout changes
  const onContainerLayout = (event: {
    nativeEvent: { layout: { width: any; height: any } };
  }) => {
    const { width, height } = event.nativeEvent.layout;
    const size = Math.min(width, height);
    setContainerSize(size);
  };

  return (
    <View
      style={[styles.wrapper, customStyles ?? {}]}
      onLayout={onContainerLayout}
    >
      <View
        style={[
          { width: containerSize, height: containerSize },
          noStyles ? {} : styles.videoContainer,
        ]}
      >
        {showVideo ? (
          <>
            {noVideoState ? (
              <View
                style={
                  children
                    ? {}
                    : [
                        styles.audioStyles,
                        {
                          width: containerSize,
                          height: containerSize,
                          borderRadius: containerSize / 2,
                        },
                      ]
                }
              >
                {children ? (
                  children
                ) : (
                  <FontAwesome
                    name={'volume-up'}
                    size={containerSize * 0.8}
                    color={colors.white}
                  />
                )}
              </View>
            ) : (
              <View style={[styles.videoContainer]}>
                {isBuffering ? (
                  <View
                    style={[
                      { width: containerSize, height: containerSize },
                      styles.center,
                    ]}
                  >
                    <ActivityIndicator
                      size="large"
                      color={colors.darkPurpleBlue}
                    />
                  </View>
                ) : null}
              </View>
            )}
          </>
        ) : (
          <PressableNeolexon
            onPress={playUrl}
            disabled={disabled}
            style={[
              styles.questionStyles,
              {
                width: containerSize,
                height: containerSize,
                borderRadius: containerSize / 2,
              },
              disabled && styles.disabled,
            ]}
          >
            <FontAwesome
              name={'question'}
              size={containerSize * 0.8}
              color={colors.white}
              solid
            />
          </PressableNeolexon>
        )}
        <Video
          ref={videoRef}
          resizeMode={ResizeMode.CONTAIN}
          source={{ uri: url }}
          style={
            !noVideoState && showVideo
              ? {
                  width: containerSize,
                  height: containerSize,
                }
              : {
                  width: 0,
                  height: 0,
                  opacity: 0,
                }
          }
          videoStyle={{
            width: containerSize,
            height: containerSize,
          }}
          onPlaybackStatusUpdate={onPlaybackStatusUpdate}
          shouldCorrectPitch
        />
      </View>
    </View>
  );
}
